import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Text } from '../ui-components';
import { colors } from '../ui-components';
import { ExternalLink } from '../ui-components';

const propTypes = {
  alignment: PropTypes.oneOf(['left', 'right', 'center']),
  image: PropTypes.shape({
    fluid: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string
    }).isRequired
  }).isRequired,
  text: PropTypes.string.isRequired
};

const defaultProps = {
  alignment: 'left'
};

/**
 * Component that renders Image with Caption
 *
 * @component ImageWithCaptionTemplate
 * 
 * @param {object} props
 * 
 * @prop {string} alignment
 * @prop {string} image
 * @prop {string} linkUrl
 * @prop {string} caption
 * @prop {string} linkText
 * @prop {string} alt
 * 
 * @return {JSX}
 */
const ImageWithCaptionTemplate = props => {
  const { alignment, image, linkUrl, caption, linkText, alt, onClickHandler } = props;

  const renderImageTitle = () => {
    let formattedTitle = '';

    if (linkUrl && linkText && caption) {
      formattedTitle = (
        <ImageWithCaptionTextWrapper alignment={alignment}>
          <CaptionText alignment={alignment}>{caption}</CaptionText>
          <ExternalLink linkstyle="secondary-link" style={{ paddingTop: '3px' }} target="_blank" href={linkUrl}>
            {linkText}
          </ExternalLink>
        </ImageWithCaptionTextWrapper>
      );
    } else if (caption) {
      formattedTitle = <CaptionText>{caption}</CaptionText>;
    }
    return formattedTitle;
  };

  return (
    <ImageWithCaptionWrapper onClick={onClickHandler ? () => onClickHandler() : null} className="image-with-caption">
      <img src={image} alt={alt} />
      {renderImageTitle()}
    </ImageWithCaptionWrapper>
  );
};

ImageWithCaptionTemplate.propTypes = propTypes;
ImageWithCaptionTemplate.defaultProps = defaultProps;

const ImageWithCaptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1;
  width: 100%;
  cursor: zoom-in;

  img {
    display: block;
    float: left;
    width: 100%;
  }
`;

const ImageWithCaptionTextWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ alignment }) => {
    switch (alignment) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      case 'center':
        return 'center';
      default:
        return 'flex-start';
    }
  }};
`;

const CaptionText = styled(props => <Text {...props} />)`
  color: ${colors.gray1};
  font-style: italic;
  text-align:  ${({ alignment }) => {
    return ['left', 'right', 'center'].includes(alignment) ? alignment : 'center'
  }}
`

export default ImageWithCaptionTemplate;
